import React, { useState, useEffect } from "react";
import { ArrowLeft, ChevronDown } from "lucide-react";

const Hero = ({ hero }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const title = hero?.title || "شريكك الموثوق في عالم العقارات";
  const subtitle = hero?.subtitle || "الوسيم العقارية";
  const description = hero?.description || "نقدم لك أفضل الخدمات العقارية";
  const cta1 = hero?.cta1_text || "استكشف خدماتنا";
  const cta2 = hero?.cta2_text || "تواصل معنا";
  const badge = hero?.badge || "الرائد في العقارات منذ 2021";
  const image = hero?.image || "https://static.prod-images.emergentagent.com/jobs/d60dcd94-e32b-483c-bd4a-5504cf1c9919/images/782543ce23903b74b94eb20d36ede9f472f465e1f5550fca50f0e8af7a9d950e.png";

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${image})` }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(105deg, rgba(23,50,105,0.97) 45%, rgba(23,50,105,0.75) 70%, rgba(23,50,105,0.3) 100%)"
        }}></div>
      </div>

      {/* Decorative circles */}
      <div className="absolute top-32 left-10 w-72 h-72 rounded-full opacity-10 float-slow"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>
      <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full opacity-8 float-medium"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>
      <div className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full opacity-5 -translate-x-1/2 -translate-y-1/2"
        style={{ border: "1px solid #ecc775" }}></div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 mb-6 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.1s" }}
          >
            <span className="bg-[#ecc775]/20 border border-[#ecc775]/50 text-[#ecc775] px-4 py-2 text-sm font-semibold rounded-sm backdrop-blur-sm">
              {badge}
            </span>
          </div>

          {/* Company Name */}
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.2s" }}
          >
            <p className="text-[#ecc775] font-bold text-xl md:text-2xl mb-3 tracking-wide">
              {subtitle}
            </p>
          </div>

          {/* Title */}
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.3s", lineHeight: "1.3" }}
            data-testid="hero-title"
          >
            {title}
          </h1>

          {/* Description */}
          <p
            className={`text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.4s" }}
          >
            {description}
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.5s" }}
          >
            <a
              href="#services"
              data-testid="hero-cta-primary"
              className="group bg-[#ecc775] text-[#173269] px-8 py-4 font-black text-lg flex items-center gap-3 hover:bg-white transition-all duration-300 shadow-xl shadow-[#ecc775]/30 rounded-sm"
            >
              {cta1}
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-secondary"
              className="border-2 border-white/60 text-white px-8 py-4 font-bold text-lg flex items-center gap-3 hover:border-[#ecc775] hover:text-[#ecc775] transition-all duration-300 rounded-sm"
            >
              {cta2}
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 flex flex-col items-center gap-2 animate-bounce">
        <ChevronDown size={28} />
      </div>
    </section>
  );
};

export default Hero;
